import React from 'react';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  DocumentTemplate,
  DocumentType,
  SavedFormRecords,
} from '~/utils/formLibrary.ts';
import { DeleteOutlinedIcon, IconButton } from '~/components/atoms/index.ts';

export interface FormsListProps {
  activeDocument: DocumentTemplate | null;
  setActiveDocument: React.Dispatch<
    React.SetStateAction<DocumentTemplate | null>
  >;
  setLoadingPdf: React.Dispatch<React.SetStateAction<boolean>>;
  setShowLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setSessionFromTemplate: (doc: DocumentTemplate) => Promise<void>;
  loadingPdf: boolean;
  formsList: DocumentTemplate[];
  handleSave: () => Promise<void | SavedFormRecords>;
  containerClass?: string;
  onDelete?: (doc: DocumentTemplate) => void;
}

export const FormsList: React.FC<FormsListProps> = ({
  activeDocument,
  setActiveDocument,
  setLoadingPdf,
  setShowLoading,
  setSessionFromTemplate,
  loadingPdf,
  formsList,
  handleSave,
  containerClass,
  onDelete,
}) => {
  const onSelectDocument = async (doc: DocumentTemplate): Promise<void> => {
    if (activeDocument === doc || loadingPdf) {
      return;
    }

    setLoadingPdf(doc?.type === DocumentType.FormTemplate);
    setShowLoading(true);

    try {
      /**
       * Update current document if its pdf & we're not in view mode
       */
      if (activeDocument?.type === DocumentType.FormTemplate) {
        // This can be queued now, we don't have to block for it
        await handleSave();
      }

      await setSessionFromTemplate(doc);
      setActiveDocument(doc);
    } finally {
      setShowLoading(false);
      setLoadingPdf(false);
    }
  };

  return (
    <div className={containerClass}>
      <List>
        {formsList.map((item) => (
          <ListItemButton
            key={item.id}
            disabled={loadingPdf}
            onClick={() => onSelectDocument(item)}
            selected={activeDocument?.id === item.id}
            alignItems="center"
          >
            <ListItemText primary={item.name} />
            {item.stateKey === 'documents' && (
              <ListItemIcon sx={{ minWidth: 'unset', marginLeft: '4px' }}>
                <IconButton
                  IconComponent={DeleteOutlinedIcon}
                  color="inherit"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.(item);
                  }}
                  disabled={loadingPdf}
                />
              </ListItemIcon>
            )}
          </ListItemButton>
        ))}
      </List>
    </div>
  );
};
